/**
 * The one sanctioned `fetch` wrapper, passed to the handed client through `ClientOptions.fetch`.
 * It exists because the API has no fault-injection route; `client.ts` is never edited.
 *
 * When `DEV_TOOLS` is false this module exports exactly the global fetch and nothing else runs.
 */
import type { Problem } from "./client";
import { DEV_TOOLS } from "./devFlag";

/** The statuses the dev footer can arm. */
export const FAULT_STATUSES = [500, 503, 409, 422, 403] as const;
export type FaultStatus = (typeof FAULT_STATUSES)[number];

const FAULT_TITLES: Record<FaultStatus, string> = {
  500: "Dev fault",
  503: "Dev fault",
  409: "Dev fault",
  422: "Dev fault",
  403: "Dev fault",
};

let armed: FaultStatus | null = null;
let lastProblemInstance: string | null = null;

type Listener = () => void;
const listeners = new Set<Listener>();
const notify = () => listeners.forEach((l) => l());

/** Subscribe to dev-tool state (armed fault, last problem instance). */
export function subscribeDevTools(listener: Listener): () => void {
  listeners.add(listener);
  return () => void listeners.delete(listener);
}

/** The `instance` of the last `Problem` the app saw, for the dev footer's state line. */
export function getLastProblemInstance(): string | null {
  return lastProblemInstance;
}

/** Which status is armed, or null. */
export function getArmedFault(): FaultStatus | null {
  return armed;
}

/**
 * Arms the NEXT `api.call` to answer a synthesised `application/problem+json` response of this
 * status. The injector clears itself after one request. Pass `null` to disarm.
 */
export function armFailNext(status: FaultStatus | null = 500): void {
  if (!DEV_TOOLS) return;
  armed = status;
  notify();
}

function faultResponse(status: FaultStatus, url: string): Response {
  const problem: Problem = {
    type: "https://levelload.provisionai.com/problems/dev-fault",
    title: FAULT_TITLES[status],
    status,
    detail: "Armed from the dev footer",
    instance: url,
  };
  return new Response(JSON.stringify(problem), {
    status,
    headers: { "content-type": "application/problem+json" },
  });
}

function recordProblem(response: Response): void {
  const type = response.headers.get("content-type") ?? "";
  if (!type.includes("problem+json")) return;
  void response
    .clone()
    .json()
    .then((body: Problem) => {
      lastProblemInstance = body.instance ?? `${body.status} ${body.type}`;
      notify();
    })
    .catch(() => undefined);
}

const wrapped: typeof globalThis.fetch = async (input, init) => {
  if (armed !== null) {
    const status = armed;
    armed = null;
    notify();
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const synthesised = faultResponse(status, url);
    recordProblem(synthesised);
    return synthesised;
  }
  const response = await globalThis.fetch(input, init);
  recordProblem(response);
  return response;
};

/** Exactly the global fetch when dev tools are compiled out. */
export const devFetch: typeof globalThis.fetch = DEV_TOOLS
  ? wrapped
  : globalThis.fetch.bind(globalThis);
